import React, { useState } from 'react';
import '../CSS/ContactUs.css';

const ContactUs: React.FC = () => {
  const imagePath = `${process.env.PUBLIC_URL || ''}/assets/Web Version/Contact Us/contact-us-banner-image.png`;
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    currentPosition: '',
    company: '',
    phone: '+1',
    email: '',
    cityOrState: '',
    preferredContact: '',
    howFoundUs: '',
    inquiry: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      preferredContact: e.target.value
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      // Production API endpoint
      const response = await fetch('/api/contact-us', {
        // const response = await fetch('http://localhost:3060/api/contact-us', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitMessage({ type: 'success', text: 'Thank you for contacting us! We will get back to you soon.' });
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          currentPosition: '',
          company: '',
          phone: '+1',
          email: '',
          cityOrState: '',
          preferredContact: '',
          howFoundUs: '',
          inquiry: ''
        });
      } else {
        setSubmitMessage({ type: 'error', text: data.error || 'Failed to submit form. Please try again.' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage({ type: 'error', text: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="contact-us-page">
      <div className="contact-us-hero-banner">
        <div 
          className="contact-us-hero-background"
          style={{ 
            backgroundImage: `url("${imagePath}")` 
          }}
        ></div>
        <div className="contact-us-hero-overlay"></div>
        <div className="contact-us-hero-content">
          <p className="contact-us-breadcrumb">Home / Contact Us</p>
          <h1 className="contact-us-hero-title">Have a question? Reach Out to Our Team.</h1>
          <p className="contact-us-hero-subtitle">Led by our partners, we connect leading corporations with proven professionals ready to deliver your vision.</p>
        </div>
      </div>

      <div className="contact-us-corporate-office">
        <h2 className="contact-us-corporate-office-title">Corporate Office:</h2>
        <div className="contact-us-corporate-office-info">
          <div className="contact-us-info-item">
            <img 
              src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Contact Us/location-icon.svg`}
              alt="Location"
              className="contact-us-info-icon"
            />
            <span className="contact-us-info-text">
              Myers McRae Consulting LLC, 116 Village Blvd Suite 200 Princeton, NJ 08540
            </span>
          </div>
          <div className="contact-us-info-item">
            <img 
              src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Contact Us/email-icon.svg`}
              alt="Email"
              className="contact-us-info-icon"
            />
            <a href="mailto:myersmcrae@myersmcrae.com" className="contact-us-info-text">
              myersmcrae@myersmcrae.com
            </a>
          </div>
          <div className="contact-us-info-item">
            <img 
              src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Contact Us/call-icon.svg`}
              alt="Phone"
              className="contact-us-info-icon"
            />
            <a href="tel:+14783306222" className="contact-us-info-text">
              478-330-6222
            </a>
          </div>
          {/* <div className="contact-us-info-item">
            <img 
              src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Contact Us/fax-icon.svg`}
              alt="Fax"
              className="contact-us-info-icon"
            />
            <span className="contact-us-info-text">
              478-330-5611
            </span>
          </div> */}
        </div>
      </div>

      <div className="contact-us-form-section">
        <form className="contact-us-form" onSubmit={handleSubmit}>
          <h2 className="contact-us-form-title">Please provide the following information</h2>
          
          <div className="contact-us-form-row">
            <div className="contact-us-form-field">
              <label htmlFor="firstName" className="contact-us-form-label">
                First Name<span className="contact-us-required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="contact-us-form-input"
                required
              />
            </div>
            <div className="contact-us-form-field">
              <label htmlFor="lastName" className="contact-us-form-label">
                Last Name<span className="contact-us-required">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="contact-us-form-input"
                required
              />
            </div>
          </div>

          <div className="contact-us-form-row">
            <div className="contact-us-form-field">
              <label htmlFor="currentPosition" className="contact-us-form-label">
                Current Position<span className="contact-us-required">*</span>
              </label>
              <input
                type="text"
                id="currentPosition"
                name="currentPosition"
                value={formData.currentPosition}
                onChange={handleInputChange}
                className="contact-us-form-input"
                required
              />
            </div>
            <div className="contact-us-form-field">
              <label htmlFor="company" className="contact-us-form-label">
                Company<span className="contact-us-required">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="contact-us-form-input"
                required
              />
            </div>
          </div>

          <div className="contact-us-form-row">
            <div className="contact-us-form-field">
              <label htmlFor="phone" className="contact-us-form-label">
                Phone<span className="contact-us-required">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="contact-us-form-input"
                required
              />
            </div>
            <div className="contact-us-form-field">
              <label htmlFor="email" className="contact-us-form-label">
                Email<span className="contact-us-required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="contact-us-form-input"
                required
              />
            </div>
          </div>

          <div className="contact-us-form-field contact-us-form-field-full">
            <label htmlFor="cityOrState" className="contact-us-form-label">
              City or State<span className="contact-us-required">*</span>
            </label>
            <input
              type="text"
              id="cityOrState"
              name="cityOrState"
              value={formData.cityOrState}
              onChange={handleInputChange}
              className="contact-us-form-input"
              required
            />
          </div>

          <div className="contact-us-form-field contact-us-form-field-full">
            <label className="contact-us-form-label">What is your preferred method of contact?</label>
            <div className="contact-us-radio-group">
              <label className="contact-us-radio-label">
                <input
                  type="radio"
                  name="preferredContact"
                  value="email"
                  checked={formData.preferredContact === 'email'}
                  onChange={handleRadioChange}
                  className="contact-us-radio-input"
                />
                <span>Email</span>
              </label>
              <label className="contact-us-radio-label">
                <input
                  type="radio"
                  name="preferredContact"
                  value="phone"
                  checked={formData.preferredContact === 'phone'}
                  onChange={handleRadioChange}
                  className="contact-us-radio-input"
                />
                <span>Phone</span>
              </label>
            </div>
          </div>

          <div className="contact-us-form-field contact-us-form-field-full">
            <label htmlFor="howFoundUs" className="contact-us-form-label">
              Please select the option that best describes how you found us
            </label>
            <select
              id="howFoundUs"
              name="howFoundUs"
              value={formData.howFoundUs}
              onChange={handleInputChange}
              className="contact-us-form-select"
            >
              <option value="">Select</option>
              <option value="google">Google</option>
              <option value="yahoo">Yahoo</option>
              <option value="other-search-engine">Other Search Engine</option>
              <option value="direct-mail">Direct Mail</option>
              <option value="business-referral">Business Referral</option>
              <option value="radio-tv">Radio/TV</option>
              <option value="magazine-newspaper">Magazine/Newspaper</option>
              <option value="word-of-mouth">Word of Mouth</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="contact-us-form-field contact-us-form-field-full">
            <label htmlFor="inquiry" className="contact-us-form-label">
              Please share your inquiry with us<span className="contact-us-required">*</span>
            </label>
            <textarea
              id="inquiry"
              name="inquiry"
              value={formData.inquiry}
              onChange={handleInputChange}
              className="contact-us-form-textarea"
              rows={6}
              required
            ></textarea>
          </div>

          <button 
            type="submit" 
            className="contact-us-form-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>

          {submitMessage && (
            <div 
              className={`contact-us-submit-message ${submitMessage.type === 'success' ? 'success' : 'error'}`}
              style={{
                marginTop: '1rem',
                padding: '1rem',
                borderRadius: '4px',
                backgroundColor: submitMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                color: submitMessage.type === 'success' ? '#155724' : '#721c24',
                border: `1px solid ${submitMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
              }}
            >
              {submitMessage.text}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ContactUs;

